import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useUser } from '@clerk/react';
import { TrustLayout } from '../../components/trust/TrustLayout';
import {
  getActiveAccount,
  acquireGraphToken,
  GRAPH_SCOPES,
} from '../../lib/graph/msalClient';
import { getMe, getMyGroups, listSites } from '../../lib/graph/sharepoint';

export const Route = createFileRoute('/trust/permissions')({
  component: PermissionsPage,
});

interface MemberOfRow {
  id: string;
  displayName?: string;
  '@odata.type'?: string;
}
interface SiteRow {
  id?: string;
  displayName?: string;
  webUrl?: string;
}

function PermissionsPage() {
  const { user, isSignedIn } = useUser();

  const [graphData, setGraphData] = useState<
    | null
    | { kind: 'loading' }
    | { kind: 'no-msal' }
    | { kind: 'error'; message: string }
    | { kind: 'ok'; me: { displayName?: string; userPrincipalName?: string }; groups: MemberOfRow[]; sites: SiteRow[] }
  >(null);

  useEffect(() => {
    if (!isSignedIn) return;
    const msal = getActiveAccount();
    if (!msal) {
      setGraphData({ kind: 'no-msal' });
      return;
    }
    let cancelled = false;
    setGraphData({ kind: 'loading' });
    (async () => {
      try {
        await acquireGraphToken();
        const [me, groups, sites] = await Promise.all([
          getMe(),
          getMyGroups(20),
          listSites(),
        ]);
        if (cancelled) return;
        setGraphData({
          kind: 'ok',
          me,
          groups: (groups.value ?? []) as MemberOfRow[],
          sites: sites as SiteRow[],
        });
      } catch (err) {
        if (cancelled) return;
        setGraphData({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn]);

  return (
    <TrustLayout>
      <h1 className="mt-0 text-[22px] text-black">Your permissions</h1>

      {!isSignedIn && (
        <p className="text-black leading-relaxed">
          Sign in to see your real Entra ID groups and SharePoint sites you have access to.
        </p>
      )}

      {isSignedIn && (
        <>
          <Section title="Your identity">
            <Field label="Clerk user">{user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? user?.id ?? '(unknown)'}</Field>
            <Field label="Repancy role">attorney (single role in v1)</Field>
            <Field label="Granted Graph scopes">{GRAPH_SCOPES.join(' ')}</Field>
          </Section>

          <Section title="Your Entra ID groups">
            {graphData === null || graphData.kind === 'loading' ? <Loading /> : null}
            {graphData?.kind === 'no-msal' && <NoMsalNote />}
            {graphData?.kind === 'error' && <ErrorNote message={graphData.message} />}
            {graphData?.kind === 'ok' && graphData.groups.length === 0 && (
              <Empty>No directory group memberships returned.</Empty>
            )}
            {graphData?.kind === 'ok' && graphData.groups.map((g) => (
              <GroupCard key={g.id} displayName={g.displayName} type={g['@odata.type']} />
            ))}
          </Section>

          <Section title="SharePoint sites you can see">
            {graphData === null || graphData.kind === 'loading' ? <Loading /> : null}
            {graphData?.kind === 'no-msal' && <NoMsalNote />}
            {graphData?.kind === 'error' && <ErrorNote message={graphData.message} />}
            {graphData?.kind === 'ok' && graphData.sites.length === 0 && (
              <Empty>
                No sites matched the default discovery query. Try connecting more sites in your tenant or use the SharePoint Import Wizard.
              </Empty>
            )}
            {graphData?.kind === 'ok' && graphData.sites.map((s) => (
              <SiteCard key={s.id} displayName={s.displayName} webUrl={s.webUrl} />
            ))}
          </Section>
        </>
      )}
    </TrustLayout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 rounded border border-black/10 bg-white px-4 py-3.5 text-black">
      <h2 className="mb-2.5 mt-0 text-sm font-bold text-black">{title}</h2>
      {children}
    </section>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 py-1 text-[13px] text-black">
      <span className="min-w-[180px] font-bold">{label}:</span>
      <span>{children}</span>
    </div>
  );
}
function Empty({ children }: { children: React.ReactNode }) {
  return <p className="my-1.5 italic text-black">{children}</p>;
}
function Loading() {
  return <Empty>Loading…</Empty>;
}
function NoMsalNote() {
  return (
    <Empty>
      Connect a Microsoft account in Settings → General → "Connect Microsoft account"
      to populate this section with your live data.
    </Empty>
  );
}
function ErrorNote({ message }: { message: string }) {
  return <p className="my-1.5 text-[#cc0000]">Graph error: {message}</p>;
}
function GroupCard({ displayName, type }: { displayName?: string; type?: string }) {
  const t = (type ?? '').replace('#microsoft.graph.', '');
  return (
    <div className="py-1.5 text-[13px] text-black">
      <strong>{displayName ?? '(unnamed)'}</strong>
      {t && <span className="ml-2 text-xs text-black">· {t}</span>}
    </div>
  );
}
function SiteCard({ displayName, webUrl, description }: { displayName?: string; webUrl?: string; description?: string }) {
  return (
    <div className="py-1.5 text-[13px] text-black">
      <strong>{displayName ?? '(unnamed site)'}</strong>
      {webUrl && (
        <>
          {' — '}
          <a href={webUrl} target="_blank" rel="noopener noreferrer" className="text-[var(--xp-selection)]">
            {webUrl}
          </a>
        </>
      )}
      {description && <div className="mt-0.5 text-xs text-black">{description}</div>}
    </div>
  );
}
